import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import logo from '../../../assets/zeos-ai-blue-logo.png';
import { StepProgress } from '../../../components/onboarding/StepProgress';
import { ZeosButton } from '../../../components/Shared/ZeosButton';
import { ZeosSpinner } from '../../../components/Shared/ZeosSpinner';
import { AuthContext } from '../../../context/AuthContext';
import { useOnboardNavAuth } from '../../../hooks/useOnboardNavAuth';
import { IUseOnboardNavUnauthProps, useOnboardNavUnauth } from '../../../hooks/useOnboardNavUnauth';
import { IZeosUser } from '../../../services/auth/IZeosUser';
import { isDemo } from '../../../services/environment/EnvironmentHelpers';
import { contentWrapperClass, ZeosFontSize } from '../../../services/helpers/ThemeHelpers';
import { createLogger } from '../../../services/Logger';
import { ONBOARDING_BASE_PATH } from '../../../services/nav/Constants';
import { IOnboardPageManager, IOnboardStatus } from '../../../services/nav/Interfaces';
import { navStepManagerBySlug, navSubstepBySlug } from '../../../services/nav/NavigationService';
import { getIsBackPage } from '../../../services/nav/QueryStringHelpers';
import { OnboardNavSlugType } from '../../../services/nav/Types';
import { isUnauthStep } from '../../../services/onboarding/OnboardHelpers';
import { IZeosUserContract } from '../../../services/zeos-api/contracts/Account';

interface IProps {
  content: React.ReactNode;
  contentType: "form" | "display" | "other";
  /** Open layout: no outer white card / footer chrome (used by Summary mockup). */
  contentLayout?: "card" | "open";
  continueLabel?: string;
  disabledMessage?: string;
  hideBackButton?: boolean;
  hideContentCard?: boolean;
  hideContinueButton?: boolean;
  hideStepIndicator?: boolean;
  isContinueDisabled?: boolean;
  isFormDisabled?: boolean;
  onNext?: (doNavigate: (state?: any) => void) => void;
  onSkip?: () => void;
  pageSlug: OnboardNavSlugType;
  pageTitle?: string;
  pageMessage?: string;
  showSkipButton?: boolean;
  showSpinner?: boolean | undefined;
  skipLabel?: string;
  statusCallback?: (status: IOnboardStatus) => void;
  triggerNext?: boolean;
  userCallback?: (user: IZeosUser) => void;
}

export const OnboardContainer = (props: IProps) => {
  const log = useMemo(() => createLogger("OnboardContainer"), []);
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [isNavigationReady, setIsNavigationReady] = useState(false);
  const [searchParams] = useSearchParams();
  const [showSpinner, setShowSpinner] = useState(props.showSpinner);
  const [zeosUser, setZeosUser] = useState<IZeosUser>();

  const currentSubstep = navSubstepBySlug(props.pageSlug);
  const isBackPage = getIsBackPage(searchParams);
  const unauthStepFlag = isUnauthStep(props.pageSlug);
  const unauthPageManager = navStepManagerBySlug(props.pageSlug);

  // Separate effect for auth observation (stable dependencies)
  useEffect(() => {
    auth.observeAuth(async (user: IZeosUserContract) => {
      const isLoggedIn = user != null;
      if (isLoggedIn) {
        const zUser = await auth.zeosUserAsync(true);
        setZeosUser(zUser);
      }
    });
  }, []);

  useEffect(() => {
    setShowSpinner(props.showSpinner);
  }, [props.showSpinner]);

  useEffect(() => {
    if (zeosUser) {
      setShowSpinner(false);
    }
  }, [zeosUser]);

  // Separate effect for user callback when zeosUser changes
  useEffect(() => {
    if (zeosUser && props.userCallback) {
      props.userCallback(zeosUser);
    }
  }, [zeosUser, props.userCallback]);

  useEffect(() => {
    // Send status to parent component
    props.statusCallback && props.statusCallback({ isBack: isBackPage });
  }, [isBackPage, props.statusCallback]);

  const showBackButton = useMemo(() => {
    return currentSubstep && currentSubstep.showBackButton;
  }, [currentSubstep]);

  const unauthNavProps: IUseOnboardNavUnauthProps = useMemo(
    () => ({
      pageManager: unauthPageManager!,
      onNext: props.onNext,
    }),
    [unauthPageManager, props.onNext]
  );

  // Navigation handlers for unauthenticated users
  const { handleNext: handleNextUnauth, handlePrev: handlePrevUnauth } =
    useOnboardNavUnauth(unauthNavProps);

  // Navigation handlers for authenticated users
  const {
    handleNext: handleNextAuth,
    handlePrev: handlePrevAuth,
    error: authError,
  } = useOnboardNavAuth(props.pageSlug);

  // Use useEffect to log errors only when they change, not on every render
  useEffect(() => {
    if (authError) {
      log.error("OnboardTemplate: Error in onboarding system", {
        authError,
      });
    }
  }, [authError]);

  // Wrapper for authenticated navigation that calls onNext callback if provided
  const handleNextAuthWithCallback = useCallback(async () => {
    if (props.onNext) {
      props.onNext(() => {
        handleNextAuth();
      });
    } else {
      await handleNextAuth();
    }
  }, [props.onNext, handleNextAuth]);

  // Select appropriate navigation handlers based on authentication status
  const nextHandler = unauthStepFlag
    ? handleNextUnauth
    : handleNextAuthWithCallback;
  const prevHandler = unauthStepFlag ? handlePrevUnauth : handlePrevAuth;

  const stepProgressPageManager: IOnboardPageManager | null = useMemo(() => {
    return unauthPageManager;
  }, [unauthPageManager]);

  const handleNext = useCallback(async () => {
    log.info("Onboarding navigation: next", {
      from: props.pageSlug,
      to: unauthPageManager?.slugNext,
    });
    await nextHandler();
  }, [nextHandler, log, props.pageSlug, unauthPageManager]);

  const handlePrev = useCallback(async () => {
    log.info("Onboarding navigation: back", {
      from: props.pageSlug,
      to: unauthPageManager?.slugPrev,
    });
    await prevHandler();
  }, [prevHandler, log, props.pageSlug, unauthPageManager]);

  // Auto-trigger navigation when triggerNext becomes true
  useEffect(() => {
    if (props.triggerNext) {
      handleNext();
    }
  }, [props.triggerNext, handleNext]);

  const handleDone = useCallback(() => {
    navigate("/portfolio");
  }, [navigate]);

  const doneLabel =
    props.pageSlug === "summary" ? "Go to Portfolio View" : "Dashboard";

  // Check if navigation is ready (either static hierarchy loaded or fallback available)
  useEffect(() => {
    const checkNavigationReady = () => {
      const manager = navStepManagerBySlug(props.pageSlug);
      if (manager) {
        setIsNavigationReady(true);
      }
    };
    checkNavigationReady();
    // Check again after a short delay to catch async loading
    const timeout = setTimeout(checkNavigationReady, 100);
    return () => clearTimeout(timeout);
  }, [props.pageSlug]);

  // Handle case where pageManager is not found
  if (!unauthPageManager || !isNavigationReady) {
    if (!isNavigationReady) {
      return (
        <div className="min-h-screen bg-zeos-grey-lt flex items-center justify-center">
          <div className="text-center">
            <div className="text-xl font-semibold mb-2">Loading...</div>
            <div className="text-gray-600">
              Setting up onboarding navigation
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-zeos-grey-lt flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl font-semibold mb-2">Page Not Found</div>
          <div className="text-gray-600">
            The requested onboarding step could not be found
          </div>
        </div>
      </div>
    );
  }

  const contentLayout = props.contentLayout ?? "card";
  const isOpenLayout = contentLayout === "open";
  const contentMaxWidth = isOpenLayout ? "max-w-[1240px]" : "max-w-[1140px]";
  const pageTitle = props.pageTitle ?? unauthPageManager.pageTitle;
  const pageMessage = props.pageMessage ?? unauthPageManager.pageMessage;

  return (
    <div id="page-container" className="min-h-screen bg-zeos-grey-lt">
      {/* 
      {zeosUser && <div className="text-gray-600">{zeosUser.emailAddress}</div>} */}
      {/* Logo Header */}
      <div className="bg-zeos-white flex justify-center h-[80px] shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
        <div className={`w-full ${contentMaxWidth} flex items-center px-6 md:px-12`}>
          <img src={logo} className="h-[40px]" alt="ZEOS-AI logo" />
        </div>
      </div>
      {/* Content Container */}
      <div className={`w-full ${contentMaxWidth} mx-auto px-5 md:px-12 py-8 md:py-9`}>
        {/* Step Title + Progress */}
        {!props.hideStepIndicator && (
          <div
            id="status-header"
            className="flex justify-between items-center mb-[2.125rem]"
          >
            <div className="text-[1.625rem] font-bold leading-tight font-sans">
              <span className="relative inline-block">
                {unauthPageManager.stepTitle}
                <span className="absolute left-0 bottom-[-0.375rem] w-[44px] h-[4px] rounded-sm bg-zeos-orange-dk"></span>
              </span>
            </div>
            <div className="hidden sm:flex justify-center">
              {stepProgressPageManager && (
                <StepProgress pageManager={stepProgressPageManager} />
              )}
            </div>
          </div>
        )}

        {/* Page Title + Message */}
        {(pageTitle || pageMessage) && (
          <div
            id="message-region"
            className={isOpenLayout ? "mb-6" : "mb-6 space-y-3"}
          >
            {pageTitle && (
              <p
                className={
                  isOpenLayout
                    ? "text-[2.125rem] font-semibold leading-snug text-zeos-black"
                    : `text-${ZeosFontSize.xxxl} leading-snug`
                }
              >
                {pageTitle}
              </p>
            )}
            {pageMessage ? (
              <p className={`text-${ZeosFontSize.xl} leading-relaxed`}>
                {pageMessage}
              </p>
            ) : null}
          </div>
        )}

        {isOpenLayout ? (
          /* Open Layout - no card chrome, page supplies its own surfaces */
          <>
            {showSpinner && <ZeosSpinner />}

            {/* Disabled Form Message */}
            {props.isFormDisabled &&
              props.disabledMessage &&
              props.disabledMessage}

            <div className="w-full">{props.content}</div>
          </>
        ) : (
          /* Main Content Card */
          <div
            className={
              props.hideContentCard ? "" : "bg-zeos-white shadow rounded p-6"
            }
          >
            {showSpinner && <ZeosSpinner />}

            {/* Disabled Form Message */}
            {props.isFormDisabled &&
              props.disabledMessage &&
              props.disabledMessage}

            <div className={contentWrapperClass(props.contentType)}>
              {props.content}
            </div>

            {!props.hideContentCard && (
              <div className="flex justify-between items-center w-full pt-8">
                {showBackButton && !props.hideBackButton ? (
                  <ZeosButton
                    label={"← Back"}
                    type="button"
                    onPress={handlePrev}
                    mode="prev"
                  />
                ) : (
                  <span></span>
                )}

                <div className="flex gap-4">
                  {props.showSkipButton && (
                    <ZeosButton
                      label={props.skipLabel ?? "Skip"}
                      type="button"
                      onPress={props.onSkip}
                      mode="prev"
                    />
                  )}

                  {/* UAPI page has no Continue: the user links (or skips), and navigation happens on its own. */}
                  {props.pageSlug === "uapi" ? (
                    // ONB-006: the redirect notice describes a state, not the page.
                    // While a utility still has to be chosen this slot stays empty,
                    // so the screen asks for one thing at a time; the notice appears
                    // only once the link has landed and triggerNext is actually
                    // firing the automatic navigation.
                    props.triggerNext && (
                      <div className="flex items-center text-gray-600 italic">
                        Utility account linked — redirecting…
                      </div>
                    )
                  ) : props.hideContinueButton ? null : !unauthPageManager.isLast ? (
                    <ZeosButton
                      label={props.continueLabel ?? "Continue"}
                      type="button"
                      onPress={handleNext}
                      mode="next"
                      isDisabled={props.isContinueDisabled}
                    />
                  ) : (
                    <ZeosButton
                      label={doneLabel}
                      type="button"
                      onPress={handleDone}
                      mode="done"
                    />
                  )}
                  {/* Special handling for Demo */}
                  {isDemo() && props.pageSlug === "account-setup" && (
                    <ZeosButton
                      label="Demo"
                      type="button"
                      onPress={() => {
                        navigate(`${ONBOARDING_BASE_PATH}/demo?autostart=1`);
                      }}
                      mode="done"
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
